import React, { useEffect, useState } from 'react';
import { HealthBuddy } from '../HealthBuddy';
import { VoiceBar, VoiceActivity, useReadThenListen } from '../VoiceBar';
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, ArrowLeftIcon, MicIcon, PhoneIcon, PlayIcon, PlusIcon, SunIcon, MoonIcon, WindIcon } from '../icons';
import { PLANS, ACTION_PLAN, MED_SCHEDULE, EMERGENCY, PlanId, Zone } from '../../data/careplans';
import { TINTS } from '../../data/mock';
export type PlanView = 'picker' | PlanId;
/**
 * Care Plans bottom modal — card-flows §3.
 *
 *   1. Picker        — which plan? Emergency Steps is coral and always last
 *   2. Action plan   — the classic three zones (Green / Amber / Red), expandable, narrated
 *   3. Med schedule  — the day's timeline; every row has a "log this" shortcut into Tracking
 *   4. Emergency     — numbered, large-type, calm steps; the buddy stays steady
 *
 * Conversational entry skips the picker (startAt prop):
 *   "show my care plan"          → action plan directly
 *   "when do I take my preventer?" → med schedule directly
 *   "I'm struggling to breathe"  → EMERGENCY STEPS IMMEDIATELY — no intermediary,
 *                                  no confirmation, top-priority routing
 *
 * Voice mode (the Sunny avatar in the modal header, same pattern as the
 * Journey): Sunny reads each view aloud and listens. In the emergency view
 * Sunny reads the steps ONE AT A TIME — "next" advances — so the user can
 * keep their hands and eyes free.
 */
export function CarePlansModal({
  onDone,
  onLogMed,
  startAt = 'picker',
  allowBack = false,
  voiceMode = false,
  onVoiceActivity,
  onViewChange
}: {
  onDone?: () => void;
  onLogMed?: () => void;
  startAt?: PlanView;
  /* keep the picker in the back stack even when deep-linked to a view */
  allowBack?: boolean;
  voiceMode?: boolean;
  onVoiceActivity?: (a: VoiceActivity) => void;
  onViewChange?: (v: PlanView) => void;
}) {
  const [view, setViewState] = useState<PlanView>(startAt);
  const cameFromPicker = startAt === 'picker' || allowBack;
  const setView = (v: PlanView) => {
    setViewState(v);
    onViewChange?.(v);
  };
  const backToPicker = cameFromPicker ? () => setView('picker') : undefined;
  if (view === 'action-plan') return <ActionPlanViewer onBack={backToPicker} onEmergency={() => setView('emergency')} voiceMode={voiceMode} onVoiceActivity={onVoiceActivity} data-id="element-329" />;
  if (view === 'med-schedule') return <MedScheduleViewer onBack={backToPicker} onLogMed={onLogMed} voiceMode={voiceMode} onVoiceActivity={onVoiceActivity} data-id="element-330" />;
  if (view === 'emergency') return <EmergencyViewer onBack={backToPicker} onDone={onDone} voiceMode={voiceMode} onVoiceActivity={onVoiceActivity} data-id="element-331" />;
  return <PlanPicker onPick={setView} voiceMode={voiceMode} onVoiceActivity={onVoiceActivity} data-id="element-332" />;
}
/* ============================================================
   1 — the plan picker
   ============================================================ */
function PlanPicker({
  onPick,
  voiceMode = false,
  onVoiceActivity
}: {
  onPick: (id: PlanId) => void;
  voiceMode?: boolean;
  onVoiceActivity?: (a: VoiceActivity) => void;
}) {
  const phase = useReadThenListen(voiceMode, onVoiceActivity, 2400);
  return <div className="px-5 pb-10 pt-2" data-id="element-333">
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-sage-deep" data-id="element-334">
        Care Plans
      </p>
      <h2 className="font-display mt-1 pr-20 text-[22px] font-semibold leading-tight text-ink" data-id="element-335">
        Your plans
      </h2>
      <p className="mt-1.5 text-[13px] leading-snug text-ink-soft" data-id="element-336">
        Agreed with your care team — always here, even offline.
      </p>

      {/* voice mode — Sunny reads the plans, then listens */}
      {voiceMode && <VoiceBar mode={phase} hint={phase === 'reading' ? 'You have three plans — your action plan, your medication schedule, and your emergency steps…' : 'Listening — just say which one you need'} sayables={phase === 'listening' ? [{
      label: 'My action plan',
      onSay: () => onPick('action-plan')
    }, {
      label: 'My medications',
      onSay: () => onPick('med-schedule')
    }, {
      label: 'Emergency steps',
      onSay: () => onPick('emergency')
    }] : undefined} data-id="element-337" />}

      <div className="dhp-stagger mt-5 flex flex-col gap-2.5" data-id="element-338">
        {PLANS.map(plan => {
        const tint = TINTS[plan.tint];
        const Icon = plan.icon;
        const isEmergency = plan.id === 'emergency';
        return <button key={plan.id} onClick={() => onPick(plan.id)} className={`flex items-center gap-3.5 rounded-card border p-4 text-left transition-all duration-300 hover:-translate-y-0.5 hover:shadow-card-hover active:scale-[0.98] ${isEmergency ? `border-coral ${tint.soft}` : `${tint.border} bg-cream shadow-card`}`} data-id="element-339">
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${tint.solid} text-cream shadow-card`} data-id="element-340">
                <Icon size={22} data-id="element-341" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-342">
                <h3 className="font-display text-[16px] font-semibold leading-tight text-ink" data-id="element-343">
                  {plan.title}
                </h3>
                <p className={`mt-0.5 text-[12px] font-medium ${tint.text}`} data-id="element-344">
                  {plan.status}
                </p>
              </span>
              <ChevronRightIcon size={16} className="shrink-0 text-ink-faint" data-id="element-345" />
            </button>;
      })}
      </div>

      {/* the voice shortcut — everything in the picker can be said instead */}
      <div className="mt-5 flex items-center gap-3 rounded-card bg-linen p-3.5" data-id="element-346">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal text-cream" data-id="element-347">
          <MicIcon size={16} data-id="element-348" />
        </span>
        <p className="text-[12px] leading-snug text-ink-soft" data-id="element-349">
          Or just tell Sunny —{' '}
          <span className="font-bold text-ink" data-id="element-350">“show my care plan”</span> or{' '}
          <span className="font-bold text-ink" data-id="element-351">
            “when do I take my preventer?”
          </span>
        </p>
      </div>
    </div>;
}
/* ============================================================
   2 — the asthma action plan (3 zones)
   ============================================================ */
function ActionPlanViewer({
  onBack,
  onEmergency,
  voiceMode = false,
  onVoiceActivity
}: {
  onBack?: () => void;
  onEmergency: () => void;
  voiceMode?: boolean;
  onVoiceActivity?: (a: VoiceActivity) => void;
}) {
  const [openZone, setOpenZone] = useState<string | null>('green');
  const [playing, setPlaying] = useState(false);
  const phase = useReadThenListen(voiceMode, onVoiceActivity, 3000);
  const expandAndOpen = (id: string) => setOpenZone(z => z === id ? null : id);
  return <div className="px-5 pb-10 pt-2" data-id="element-352">
      <ViewerHeader meta="Care Plans · Action plan" title="Your Asthma Action Plan" onBack={onBack} data-id="element-353" />
      <p className="mt-1.5 text-[12px] font-medium text-sage-deep" data-id="element-354">
        {ACTION_PLAN.reviewed}
      </p>

      {/* narration — Sunny reads the plan aloud (touch mode) */}
      {!voiceMode && <button onClick={() => setPlaying(p => !p)} className={`mt-3.5 inline-flex w-fit items-center gap-2 rounded-full px-3.5 py-2 transition-colors ${playing ? 'bg-teal text-cream' : 'bg-teal-soft text-teal-deep'}`} data-id="element-355">
          <PlayIcon size={12} data-id="element-356" />
          <span className="text-[12px] font-bold" data-id="element-357">
            {playing ? 'Sunny is reading…' : 'Listen with Sunny'}
          </span>
        </button>}

      {/* voice mode — Sunny reads the zones, then listens */}
      {voiceMode && <VoiceBar mode={phase} hint={phase === 'reading' ? 'Your plan has three zones — green when you’re well, amber when things get worse, red for an emergency…' : 'Listening — say a zone to hear it, or “emergency steps”'} sayables={phase === 'listening' ? [{
      label: 'Green zone',
      onSay: () => setOpenZone('green')
    }, {
      label: 'Amber zone',
      onSay: () => setOpenZone('amber')
    }, {
      label: 'Red zone',
      onSay: () => setOpenZone('red')
    }, {
      label: 'Emergency steps',
      onSay: onEmergency
    }] : undefined} data-id="element-358" />}

      {/* the three zones — stacked, expandable */}
      <div className="mt-5 flex flex-col gap-2.5" data-id="element-359">
        {ACTION_PLAN.zones.map(zone => <ZoneCard key={zone.id} zone={zone} open={openZone === zone.id} onToggle={() => expandAndOpen(zone.id)} onEmergency={onEmergency} data-id="element-360" />)}
      </div>

      {/* the buddy keeps the user company while narrating */}
      {(playing || voiceMode && phase === 'reading') && <div className="mt-5 flex justify-center" data-id="element-361">
          <HealthBuddy size={52} mood="speaking" data-id="element-362" />
        </div>}
    </div>;
}
function ZoneCard({
  zone,
  open,
  onToggle,
  onEmergency
}: {
  zone: Zone;
  open: boolean;
  onToggle: () => void;
  onEmergency: () => void;
}) {
  const tint = TINTS[zone.tint];
  const isRed = zone.id === 'red';
  return <div className={`overflow-hidden rounded-card border-2 transition-all duration-300 ${open ? tint.soft : 'border-linen-deep bg-cream'}`} style={open ? {
    borderColor: `var(--dhp-${zone.tint})`
  } : undefined} data-id="element-363">
      {/* zone header — always visible */}
      <button onClick={onToggle} className="flex w-full items-center gap-3 p-4 text-left" data-id="element-364">
        <span className={`h-10 w-10 shrink-0 rounded-full ${tint.solid} shadow-card`} data-id="element-365" />
        <span className="min-w-0 flex-1" data-id="element-366">
          <p className={`text-[10.5px] font-bold uppercase tracking-[0.14em] ${tint.text}`} data-id="element-367">
            {zone.name}
          </p>
          <h3 className="font-display text-[16.5px] font-semibold leading-tight text-ink" data-id="element-368">
            {zone.headline}
          </h3>
        </span>
        <ChevronDownIcon size={16} className={`shrink-0 text-ink-faint transition-transform duration-300 ${open ? 'rotate-180' : ''}`} data-id="element-369" />
      </button>

      {/* zone body — signs + actions */}
      {open && <div className="dhp-fade-up px-4 pb-4" data-id="element-370">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink-faint" data-id="element-371">
            How it feels
          </p>
          <ul className="mt-1.5 flex flex-col gap-1" data-id="element-372">
            {zone.signs.map((sign, i) => <li key={i} className="flex items-start gap-2 text-[13.5px] leading-snug text-ink-soft" data-id="element-373">
                <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${tint.solid}`} data-id="element-374" />
                {sign}
              </li>)}
          </ul>

          <p className="mt-3.5 text-[11px] font-bold uppercase tracking-[0.14em] text-ink-faint" data-id="element-375">
            What to do
          </p>
          <ul className="mt-1.5 flex flex-col gap-1.5" data-id="element-376">
            {zone.actions.map((action, i) => <li key={i} className="flex items-start gap-2.5 text-[13.5px] font-medium leading-snug text-ink" data-id="element-377">
                <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${tint.solid} text-cream`} data-id="element-378">
                  <CheckIcon size={10} strokeWidth={2.5} data-id="element-379" />
                </span>
                {action}
              </li>)}
          </ul>

          {/* the red zone routes into the emergency steps */}
          {isRed && <button onClick={onEmergency} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-coral py-3.5 text-[14px] font-bold text-cream shadow-card transition-transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-380">
              Open emergency steps
              <ChevronRightIcon size={14} strokeWidth={2.2} data-id="element-381" />
            </button>}
        </div>}
    </div>;
}
/* ============================================================
   3 — the medication schedule
   ============================================================ */
function MedScheduleViewer({
  onBack,
  onLogMed,
  voiceMode = false,
  onVoiceActivity
}: {
  onBack?: () => void;
  onLogMed?: () => void;
  voiceMode?: boolean;
  onVoiceActivity?: (a: VoiceActivity) => void;
}) {
  const phase = useReadThenListen(voiceMode, onVoiceActivity, 2800);
  const slotIcon = (slot: string) => slot === 'Morning' ? <SunIcon size={16} data-id="element-382" /> : slot === 'Evening' ? <MoonIcon size={16} data-id="element-383" /> : <WindIcon size={16} data-id="element-384" />;
  return <div className="px-5 pb-10 pt-2" data-id="element-385">
      <ViewerHeader meta="Care Plans · Medication" title="Your medication schedule" onBack={onBack} data-id="element-386" />
      <p className="mt-1.5 text-[12px] font-medium text-sky-deep" data-id="element-387">
        {MED_SCHEDULE.summary}
      </p>

      {/* voice mode — Sunny reads the schedule, then listens */}
      {voiceMode && <VoiceBar mode={phase} hint={phase === 'reading' ? 'Your preventer is two puffs, morning and evening — and your reliever is there whenever you need it…' : 'Listening — say “log my evening dose” to log it'} sayables={phase === 'listening' && onLogMed ? [{
      label: 'Log my evening dose',
      onSay: onLogMed
    }] : undefined} data-id="element-388" />}

      {/* the day's timeline */}
      <div className="relative mt-6" data-id="element-389">
        <div className="absolute bottom-6 left-[21px] top-6 w-[2px] rounded-full bg-linen-deep" data-id="element-390" />

        <div className="flex flex-col gap-3" data-id="element-391">
          {MED_SCHEDULE.meds.map(med => {
          const isReliever = med.kind === 'reliever';
          const tint = TINTS[isReliever ? 'sky' : 'coral'];
          return <div key={med.id} className="relative flex items-start gap-3.5" data-id="element-392">
                {/* timeline node */}
                <span className={`relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full shadow-card ${med.taken ? 'bg-sage text-cream' : `${tint.soft} ${tint.text}`}`} data-id="element-393">
                  {med.taken ? <CheckIcon size={16} strokeWidth={2.5} data-id="element-394" /> : slotIcon(med.slot)}
                </span>

                {/* med card */}
                <div className="min-w-0 flex-1 rounded-card border border-linen-deep bg-cream p-3.5 shadow-card" data-id="element-395">
                  <div className="flex items-start justify-between gap-2" data-id="element-396">
                    <div className="min-w-0" data-id="element-397">
                      <p className={`text-[10.5px] font-bold uppercase tracking-[0.14em] ${tint.text}`} data-id="element-398">
                        {med.slot}
                        {med.taken === true && ' · taken ✓'}
                      </p>
                      <h3 className="font-display mt-0.5 text-[15.5px] font-semibold leading-tight text-ink" data-id="element-399">
                        {med.name} · {med.dose}
                      </h3>
                      <p className="mt-0.5 text-[12px] leading-snug text-ink-soft" data-id="element-400">
                        {med.detail}
                      </p>
                    </div>
                    {/* the inhaler colour cue */}
                    <span className={`mt-1 h-3 w-8 shrink-0 rounded-full ${isReliever ? 'bg-sky' : 'bg-coral'}`} data-id="element-401" />
                  </div>

                  {/* "log this" — the shortcut into Tracking */}
                  {med.taken !== true && <button onClick={onLogMed} className={`mt-3 inline-flex items-center gap-1.5 rounded-full ${tint.soft} px-3.5 py-2 text-[12px] font-bold ${tint.text} transition-all hover:-translate-y-0.5 active:scale-95`} data-id="element-402">
                      <PlusIcon size={12} strokeWidth={2.4} data-id="element-403" />
                      Log this
                    </button>}
                </div>
              </div>;
        })}
        </div>
      </div>

      {/* the assistant can do this too */}
      <div className="mt-6 flex items-center gap-3 rounded-card bg-linen p-3.5" data-id="element-404">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal text-cream" data-id="element-405">
          <MicIcon size={16} data-id="element-406" />
        </span>
        <p className="text-[12px] leading-snug text-ink-soft" data-id="element-407">
          Logging a dose takes one tap — or just say{' '}
          <span className="font-bold text-ink" data-id="element-408">“log my evening preventer”</span>
        </p>
      </div>
    </div>;
}
/* ============================================================
   4 — the emergency steps
   ============================================================ */
/* Numbered, large-type, calm. Minimal chrome — nothing on this screen that
   isn't needed in an emergency. The buddy switches to a calm, steady mood.
   Voice mode reads the steps ONE AT A TIME so hands and eyes stay free. */
function EmergencyViewer({
  onBack,
  onDone,
  voiceMode = false,
  onVoiceActivity
}: {
  onBack?: () => void;
  onDone?: () => void;
  voiceMode?: boolean;
  onVoiceActivity?: (a: VoiceActivity) => void;
}) {
  /* voice: which step Sunny is reading / has read up to */
  const [voiceStep, setVoiceStep] = useState(0);
  const [voicePhase, setVoicePhase] = useState<'reading' | 'listening'>('reading');
  /* bumping the nonce re-reads the current step ("read that again") */
  const [readNonce, setReadNonce] = useState(0);
  /* read the current step aloud, then listen for "next" */
  useEffect(() => {
    if (!voiceMode) {
      onVoiceActivity?.('idle');
      return;
    }
    setVoicePhase('reading');
    onVoiceActivity?.('speaking');
    const t = setTimeout(() => {
      setVoicePhase('listening');
      onVoiceActivity?.('listening');
    }, 2800);
    return () => clearTimeout(t);
  }, [voiceMode, voiceStep, readNonce]);
  const steps = EMERGENCY.steps;
  const atLastStep = voiceStep >= steps.length - 1;
  const currentStep = steps[voiceStep];
  return <div className="px-5 pb-10 pt-2" data-id="element-409">
      {/* header — calm, not alarming */}
      <div className="flex items-center gap-2" data-id="element-410">
        {onBack && <button onClick={onBack} className="flex h-8 w-8 items-center justify-center rounded-full bg-linen text-ink-soft transition-transform active:scale-90" data-id="element-411">
            <ArrowLeftIcon size={15} data-id="element-412" />
          </button>}
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-coral-deep" data-id="element-413">
          Emergency steps
        </p>
      </div>
      <h2 className="font-display mt-1 pr-20 text-[26px] font-semibold leading-tight text-ink" data-id="element-414">
        {EMERGENCY.title}
      </h2>

      {/* the buddy stays calm and steady — it doesn't panic, so you don't */}
      <div className="mt-4 flex items-center gap-3 rounded-card bg-linen p-3.5" data-id="element-415">
        <HealthBuddy size={44} mood={voiceMode && voicePhase === 'reading' ? 'speaking' : 'calm'} className="shrink-0" data-id="element-416" />
        <p className="text-[13.5px] font-medium leading-snug text-ink" data-id="element-417">
          {EMERGENCY.intro}
        </p>
      </div>

      {/* voice mode — Sunny reads step by step; "next" advances */}
      {voiceMode && <VoiceBar mode={voicePhase} hint={voicePhase === 'reading' ? `Step ${currentStep.n} — ${currentStep.spoken}` : atLastStep ? 'Listening — say “read it again” to start over, or “done” to close' : 'Listening — say “next” when you’re ready'} sayables={voicePhase === 'listening' ? atLastStep ? [{
      label: 'Read it again',
      onSay: () => voiceStep === 0 ? setReadNonce(n => n + 1) : setVoiceStep(0)
    }, {
      label: 'Done',
      onSay: () => onDone?.()
    }] : [{
      label: 'Next',
      onSay: () => setVoiceStep(s => s + 1)
    }, {
      label: 'Read that again',
      onSay: () => setReadNonce(n => n + 1)
    }] : undefined} data-id="element-418" />}

      {/* the steps — numbered, large-type, all visible (nothing hidden in an emergency) */}
      <div className="mt-5 flex flex-col gap-2.5" data-id="element-419">
        {steps.map((step, i) => {
        const isCurrent = voiceMode && i === voiceStep;
        const isPast = voiceMode && i < voiceStep;
        return <div key={step.n} className={`flex items-start gap-3.5 rounded-card border-2 p-4 transition-all duration-300 ${isCurrent ? 'border-coral bg-coral-soft shadow-card-hover' : voiceMode ? 'border-linen-deep bg-cream opacity-60' : 'border-linen-deep bg-cream'}`} data-id="element-420">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-[18px] font-semibold ${isPast ? 'bg-sage text-cream' : isCurrent ? 'bg-coral text-cream' : 'bg-linen text-ink'}`} data-id="element-421">
                {isPast ? <CheckIcon size={16} strokeWidth={2.5} data-id="element-422" /> : step.n}
              </span>
              <div className="min-w-0 pt-0.5" data-id="element-423">
                <h3 className="font-display text-[18px] font-semibold leading-tight text-ink" data-id="element-424">
                  {step.title}
                </h3>
                <p className="mt-1 text-[14.5px] leading-snug text-ink-soft" data-id="element-425">
                  {step.detail}
                </p>
              </div>
            </div>;
      })}
      </div>

      {/* call 999 — always present, always reachable */}
      <button className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-coral text-[17px] font-bold text-cream shadow-card transition-transform hover:scale-[1.02] active:scale-[0.98]" style={{
      paddingTop: 18,
      paddingBottom: 18
    }} data-id="element-426">
        <PhoneIcon size={18} strokeWidth={2.2} data-id="element-427" />
        Call 999
      </button>
      <p className="mt-2.5 text-center text-[12px] font-medium text-ink-soft" data-id="element-428">
        Tell them it's an asthma attack. They will stay on the line with you.
      </p>
    </div>;
}
/* ============================================================
   shared bits
   ============================================================ */
function ViewerHeader({
  meta,
  title,
  onBack
}: {
  meta: string;
  title: string;
  onBack?: () => void;
}) {
  return <>
      <div className="flex items-center gap-2" data-id="element-429">
        {onBack && <button onClick={onBack} className="flex h-8 w-8 items-center justify-center rounded-full bg-linen text-ink-soft transition-transform active:scale-90" data-id="element-430">
            <ArrowLeftIcon size={15} data-id="element-431" />
          </button>}
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-sage-deep" data-id="element-432">
          {meta}
        </p>
      </div>
      <h2 className="font-display mt-1 pr-20 text-[22px] font-semibold leading-tight text-ink" data-id="element-433">
        {title}
      </h2>
    </>;
}